import React, { useState, useEffect, useRef } from "react";
import { predictCrop, predictPestDisease, predictStage, compressImage, raiseVerification, getVerificationTickets, acknowledgeVerification } from "../api";
import VerifiedReportsModal from "./VerifiedReportsModal";

const normalizeCropLabel = (value) => {
  if (value === null || value === undefined) return "undefined";
  const text = String(value).trim();
  const lower = text.toLowerCase();
  if (!text || lower === "undefined" || lower === "null" || lower === "unidentified" || lower === "unknown") {
    return "undefined";
  }
  return text;
};

const normalizePestDiseaseLabel = (value) => {
  if (value === null || value === undefined) return "None";
  const text = String(value).trim();
  const lower = text.toLowerCase();
  if (
    !text ||
    lower === "undefined" ||
    lower === "null" ||
    lower === "none" ||
    lower === "healthy" ||
    lower.includes("no pest") ||
    lower.includes("no disease")
  ) {
    return "None";
  }
  return text;
};

const normalizePrediction = (pred) => {
  if (!pred) pred = {};
  const crop = {
    label: "Unknown",
    confidence: 0.0,
    ...(pred.crop || {})
  };
  crop.label = normalizeCropLabel(crop.label);
  const stage = {
    label: "Unknown",
    confidence: 0.0,
    timeline: [],
    activeStageIndex: 0,
    description: "",
    ...(pred.stage || {})
  };
  stage.label = normalizePestDiseaseLabel(stage.label);
  const pest = {
    crop: (crop.label || "Unknown").toUpperCase(),
    disease: "None",
    pest: "None",
    stress: "None",
    stressConfidence: 0.0,
    cropConfidence: crop.confidence || 0.0,
    diseaseConfidence: 0.95,
    pestConfidence: 0.95,
    category: "None",
    finalLabel: `${crop.label || "Unknown"}-Healthy`,
    symptoms: `${crop.label || "Unknown"} foliage appears healthy with no visible symptoms.`,
    diagnosticReasoning: "No pathological signs observed.",
    recommendations: "",
    boxes: [],
    ...(pred.pest || {})
  };
  pest.disease = normalizePestDiseaseLabel(pest.disease);
  pest.pest = normalizePestDiseaseLabel(pest.pest);
  pest.stress = normalizePestDiseaseLabel(pest.stress);
  pest.category = normalizePestDiseaseLabel(pest.category);

  const isHealthy = pest.disease === "None" && pest.pest === "None";
  pest.recommendations = isHealthy ? "" : (pest.recommendations || "");

  return { crop, stage, pest };
};

const getImageUrl = (img) => {
  if (!img) return "";
  if (
    img.startsWith("data:") ||
    img.startsWith("http://") ||
    img.startsWith("https://") ||
    img.startsWith("/") ||
    img.startsWith("static/") ||
    img.startsWith("blob:")
  ) {
    return img;
  }
  return `data:image/jpeg;base64,${img}`;
};

// ─── All supported crops, their diseases, and growth stages (from agronomic_db.py) ───
const CROP_DATA = [
  {
    name: "Wheat",
    icon: "grain",
    image: "https://lh3.googleusercontent.com/aida-public/AB6AXuBgEPELAiiTovidireQ_7PnQ9BpTX0g4sbjzWAxEpP_chepwjIm3Ly7D9_1Dwos-r1kNBXhk4TDi0vtPONl9NKOeTuI3EUXH-Hix--Nx9_49prR6dnnLhLICqaiVhziIViy-LrCeJ-RleIQQQ3Kunx80wjH0dQsgQA4NKH4jJ34RWbWBKRZB96X_HnGwGFtczeEJ0fTY2B76k-8jyfz2wRFAOlzK8llSmoc98YiLosPRtd8xif_3PHO",
    diseases: [
      { name: "Flag Smut", key: "wheat_flagsmut" },
      { name: "Fusarium Foot Rot", key: "wheat_fusariumfootrot" },
      { name: "Leaf Blight", key: "wheat_leafblight" },
      { name: "Powdery Mildew", key: "wheat_powderymildew" },
      { name: "Rust", key: "wheat_rust" },
      { name: "Healthy", key: "wheat_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"]
  },
  {
    name: "Paddy",
    icon: "rice_bowl",
    image: "https://lh3.googleusercontent.com/aida-public/AB6AXuB5YulULp1Y0xcfHo0Bxs63AeLanmPw1TXP7LYIFrK2YBWzcjfhEtwKzbFMeu-mHXMyzzQTwjgYmV1dxkoyxHpZS9aBz5PdkFkCvt-YrOKB2i0Th-B451_aQvyGpTxbE3DPkV6y1xL-GcYYvnD8WyjfAYEXoMS9UrhAegMxNWomvzbE9h79h4l_QPYKpC2ACrgKNIVaIVUZvLNwKBQfVW1h4cy-lKv85Pb2zetkUZ_C-QC7HeMVc3Yx",
    diseases: [
      { name: "Bacterial Blight", key: "paddy_bacterialblight" },
      { name: "Blast", key: "paddy_blast" },
      { name: "Brown Spot", key: "paddy_brownspot" },
      { name: "Tungro", key: "paddy_tungro" },
      { name: "Healthy", key: "paddy_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"]
  },
  {
    name: "Maize",
    icon: "spa",
    image: "https://lh3.googleusercontent.com/aida-public/AB6AXuD4cnGTUYZOMMOWXNu1dNQJu58MxMn7y1rQLA7ZNxtyF_hDkptPFHZzVoe48vvOsphW5908t-oJW-4Y-Y6CDvZyifq7jfVizTy3_33mN8k8uXkEzxR1iTisBDX-YeZ1bdoXRFk5KF9boUCtxMIiypUCISAgU9Dfu9J1Kc6KSvDAfHgYq-U_rT9mBJTARKwaFx2PUG_KEuAFzXf7RGa0MspQqYJS8BpQbPIHkiu9DJawdnfODNlcornU",
    diseases: [
      { name: "Ear Rot", key: "maize_earrot" },
      { name: "Leaf Blight", key: "maize_leafblight" },
      { name: "Rust", key: "maize_rust" },
      { name: "Leaf Spot", key: "maize_leafspot" },
      { name: "Streak Virus", key: "maize_streakvirus" },
      { name: "Healthy", key: "maize_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"]
  },
  {
    name: "Cotton",
    icon: "cloud",
    image: process.env.PUBLIC_URL + "/cotton_crop.png",
    diseases: [
      { name: "Anthracnose", key: "cotton_anthracnose" },
      { name: "Bacterial Blight", key: "cotton_bacterialblight" },
      { name: "Leaf Curl", key: "cotton_leafcurl" },
      { name: "Powdery Mildew", key: "cotton_powderymildew" },
      { name: "Target Spot", key: "cotton_targetspot" },
      { name: "Healthy", key: "cotton_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Flowering Squaring", "Boll Development", "Boll Bursting Maturity"]
  },
  {
    name: "Chickpea",
    icon: "eco",
    image: process.env.PUBLIC_URL + "/chickpea_crop.png",
    diseases: [
      { name: "Ascochyta Blight", key: "chickpea_asochytablight" },
      { name: "Botrytis Gray Mold", key: "chickpea_botrytisgraymold" },
      { name: "Dry Root Rot", key: "chickpea_dryrootrot" },
      { name: "Fusarium Wilt", key: "chickpea_fusariumwilt" },
      { name: "Healthy", key: "chickpea_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"]
  },
  {
    name: "Soybean",
    icon: "grass",
    image: "https://lh3.googleusercontent.com/aida-public/AB6AXuA-afczAcuyVSNUbCNGc47FeAZOU2oDjTzULXF-EDKJs_wetEIWO9_5L0CBMc3GhnuOLUzK-SeLlvKSur9eUFD3gHm2VOQmB-0zw-doMExEUg-MxclzkmCtVqF2c5LfVxirll7q1zZ-CX6JURcqu9Gf4KQj8PXuolDYaovPVFi0VcfvpZol2cY6TgWNtwbzhwUyqPaN6DNE6lW3kRA9zRWdNwzkVQUoh9OtC93iVORVbZp7jBySRAd-",
    diseases: [
      { name: "Bacterial Blight", key: "soybean_bacterialblight" },
      { name: "Cercospora Leaf Blight", key: "soybean_cercosporaleafblight" },
      { name: "Downy Mildew", key: "soybean_downeymildew" },
      { name: "Frogeye Spot", key: "soybean_frogeyespot" },
      { name: "Rust", key: "soybean_rust" },
      { name: "Target Spot", key: "soybean_targetspot" },
      { name: "Healthy", key: "soybean_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Pod Development", "Maturity"]
  },
  {
    name: "Mustard",
    icon: "local_florist",
    image: "https://lh3.googleusercontent.com/aida-public/AB6AXuBgEPELAiiTovidireQ_7PnQ9BpTX0g4sbjzWAxEpP_chepwjIm3Ly7D9_1Dwos-r1kNBXhk4TDi0vtPONl9NKOeTuI3EUXH-Hix--Nx9_49prR6dnnLhLICqaiVhziIViy-LrCeJ-RleIQQQ3Kunx80wjH0dQsgQA4NKH4jJ34RWbWBKRZB96X_HnGwGFtczeEJ0fTY2B76k-8jyfz2wRFAOlzK8llSmoc98YiLosPRtd8xif_3PHO",
    diseases: [
      { name: "Alternaria Blight", key: "mustard_alternariablight" },
      { name: "White Rust", key: "mustard_whiterust" },
      { name: "Downy Mildew", key: "mustard_downeymildew" },
      { name: "Powdery Mildew", key: "mustard_powderymildew" },
      { name: "Sclerotinia Rot", key: "mustard_sclerotiniarot" },
      { name: "Healthy", key: "mustard_healthy" }
    ],
    stages: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Pod Development", "Maturity"]
  }
];

export default function CropReport({ onBack, onToggleSidebar, isSidebarOpen, onLogout }) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);
  const [uiState, setUiState] = useState(1); // 1: Upload, 2: Single Result, 3: Folder Batch Processing, 4: Catalog Mode
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Navigation input references
  const anotherInputRef = useRef(null);
  const anotherFolderInputRef = useRef(null);
  const thumbnailStripRef = useRef(null);

  // File Upload states
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingImage, setProcessingImage] = useState(null);

  // Single Result states (State 2)
  const [singleResult, setSingleResult] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [verificationSent, setVerificationSent] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (singleResult?.isFlagged) {
      setVerificationSent(true);
    } else {
      setVerificationSent(false);
    }
  }, [singleResult]);

  const handleFlagVerification = async () => {
    if (!singleResult) return;
    setLoading(true);
    try {
      let imgData = singleResult.image;
      if (imgData && !imgData.startsWith("data:") && (imgData.startsWith("/") || imgData.startsWith("http"))) {
        try {
          const res = await fetch(imgData);
          const blob = await res.blob();
          imgData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Failed to convert image URL to base64 for verification", e);
        }
      }

      const sessionId = localStorage.getItem("cropscan_session_id");
      const predictionWithSource = { ...singleResult.prediction, source_module: "report" };
      await raiseVerification(sessionId, imgData, predictionWithSource);
      setVerificationSent(true);
      await loadTicketsAndHistory();
    } catch (err) {
      console.error(err);
      alert("Failed to send verification request.");
    } finally {
      setLoading(false);
    }
  };
  const [panPos, setPanPos] = useState({ x: 0, y: 0 });
  const [isPanActive, setIsPanActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState([]);
  const [userTickets, setUserTickets] = useState([]);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  const loadTicketsAndHistory = React.useCallback(async () => {
    const sessionId = localStorage.getItem("cropscan_session_id");
    const saved = localStorage.getItem("crop_report_history_v3");
    let localHistory = [];
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        localHistory = parsed.map(item => ({
          ...item,
          prediction: normalizePrediction(item.prediction)
        }));
      } catch (e) {
        console.warn("Failed to parse history", e);
      }
    } else {
      const defaultHistory = [
        {
          id: "rep-1",
          filename: "Maize.jpg",
          date: "Aug 05, 2026",
          size: "2.4 MB",
          thumbnail: "/assets/recent/maize-healthy.jpg",
          prediction: {
            crop: { label: "Maize", confidence: 0.95 },
            stage: { label: "Grain Filling", confidence: 0.94 }
          }
        },
        {
          id: "rep-2",
          filename: "corn_edge_field_7.png",
          date: "Aug 04, 2026",
          size: "1.8 MB",
          thumbnail: "/assets/recent/maize-leaf-spot.jpg",
          prediction: {
            crop: { label: "Maize", confidence: 0.89 },
            stage: { label: "Maturity", confidence: 0.91 }
          }
        },
        {
          id: "rep-3",
          filename: "soybean-bacterial-blight.jpg",
          date: "Aug 01, 2026",
          size: "1.6 MB",
          thumbnail: "/assets/recent/soybean-bacterial-blight.jpg",
          prediction: {
            crop: { label: "Soybean", confidence: 0.87 },
            stage: { label: "Maturity", confidence: 0.88 }
          }
        }
      ].map(item => ({
        ...item,
        prediction: normalizePrediction(item.prediction)
      }));
      localHistory = defaultHistory;
      localStorage.setItem("crop_report_history_v3", JSON.stringify(defaultHistory));
    }

    if (sessionId) {
      try {
        const res = await getVerificationTickets(sessionId);
        if (res.status === "success") {
          const tickets = res.tickets || [];
          setUserTickets(tickets);
          const flaggedHistory = tickets.map(t => {
            const orig = t.original_prediction || {};
            const val = t.validated_prediction || {};
            return {
              id: t.ticket_id,
              filename: `Flagged Scan (${t.ticket_id})`,
              date: new Date(t.timestamp * 1000).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
              size: "1.2 MB",
              thumbnail: t.image,
              prediction: {
                crop: {
                  label: val.crop || orig.crop?.label || orig.crop || "Unknown",
                  confidence: 1.0
                },
                stage: {
                  label: val.stage || orig.stage?.label || orig.stage || "Unknown",
                  confidence: 1.0
                },
                pest: {
                  disease: val.disease || orig.pest?.disease || orig.disease || "None",
                  pest: val.pest || orig.pest?.pest || orig.pest || "None",
                  stress: val.category || orig.pest?.stress || orig.category || "None",
                  symptoms: val.recommendations ? "Agronomist review completed." : (orig.pest?.symptoms || "No symptoms available."),
                  recommendations: val.recommendations || orig.pest?.recommendations || "No recommendations available."
                }
              },
              isFlagged: true,
              status: t.status,
              validated_prediction: val
            };
          });
          const filteredLocal = localHistory.filter(lh => !flaggedHistory.some(fh => fh.id === lh.id));
          setHistory([...flaggedHistory, ...filteredLocal]);
        }
      } catch (err) {
        console.warn("Failed to load user tickets", err);
        setHistory(localHistory);
      }
    } else {
      setHistory(localHistory);
    }
  }, []);

  useEffect(() => {
    loadTicketsAndHistory();
  }, [loadTicketsAndHistory]);

  // Folder Processing states (State 3)
  const [processedResults, setProcessedResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [flaggedImages, setFlaggedImages] = useState({});

  const handleFlagFolderImage = async () => {
    if (!processedResults[activeIndex]) return;
    setLoading(true);
    try {
      let imgData = processedResults[activeIndex].image;
      if (imgData && !imgData.startsWith("data:") && (imgData.startsWith("/") || imgData.startsWith("http"))) {
        try {
          const res = await fetch(imgData);
          const blob = await res.blob();
          imgData = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          console.warn("Failed to convert image URL to base64 for verification", e);
        }
      }

      const sessionId = localStorage.getItem("cropscan_session_id");
      const predictionWithSource = { ...processedResults[activeIndex].prediction, source_module: "report" };
      await raiseVerification(sessionId, imgData, predictionWithSource);
      setFlaggedImages(prev => ({ ...prev, [activeIndex]: true }));
      await loadTicketsAndHistory();
    } catch (err) {
      console.error(err);
      alert("Failed to send verification request.");
    } finally {
      setLoading(false);
    }
  };

  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [folderLoadingImage, setFolderLoadingImage] = useState(null);
  const [summaryTab, setSummaryTab] = useState("crop"); // "crop" | "stage" | "infestation"
  // Catalog tab states
  const [searchQuery, setSearchQuery] = useState("");

  // Restore prediction session from sessionStorage on mount (for page switches)
  useEffect(() => {
    const savedSession = sessionStorage.getItem("crop_report_session");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.uiState === 2 && session.singleResult) {
          setSingleResult({
            ...session.singleResult,
            prediction: normalizePrediction(session.singleResult.prediction)
          });
          setUiState(2);
        } else if (session.uiState === 3 && session.processedResults && session.processedResults.length > 0) {
          const normalizedResults = session.processedResults.map(r => ({
            ...r,
            prediction: normalizePrediction(r.prediction)
          }));
          setProcessedResults(normalizedResults);
          setActiveIndex(session.activeIndex || 0);
          setUiState(3);
        }
      } catch (e) {
        console.warn("Failed to parse saved crop_report_session", e);
      }
    }
  }, []);

  const persistSingleSession = async (resultEntry) => {
    try {
      let compressedImg = resultEntry.image;
      try {
        compressedImg = await compressImage(resultEntry.image, 600, 600, 0.7);
      } catch (e) {
        console.warn("Failed to compress single result image for sessionStorage", e);
      }
      sessionStorage.setItem("crop_report_session", JSON.stringify({
        uiState: 2,
        singleResult: {
          ...resultEntry,
          image: compressedImg
        }
      }));
    } catch (e) {
      console.warn("Failed to save crop report single session to sessionStorage", e);
    }
  };

  const persistFolderSession = async (results, idx = 0) => {
    try {
      const compressedResults = await Promise.all(results.map(async (res) => {
        try {
          const comp = await compressImage(res.image, 400, 400, 0.6);
          return { ...res, image: comp };
        } catch (_) {
          return res;
        }
      }));
      sessionStorage.setItem("crop_report_session", JSON.stringify({
        uiState: 3,
        processedResults: compressedResults,
        activeIndex: idx
      }));
    } catch (e) {
      console.warn("Failed to save crop report folder session to sessionStorage", e);
    }
  };

  const clearSession = () => {
    try {
      sessionStorage.removeItem("crop_report_session");
    } catch (e) { }
  };

  // Auto-sync session state to sessionStorage
  useEffect(() => {
    if (uiState === 1) {
      clearSession();
    } else if (uiState === 2 && singleResult) {
      persistSingleSession(singleResult);
    } else if (uiState === 3 && processedResults.length > 0) {
      persistFolderSession(processedResults, activeIndex);
    }
  }, [uiState, singleResult, processedResults, activeIndex]);


  const saveToHistory = async (filename, prediction, imgData) => {
    let thumb = imgData;
    try {
      thumb = await compressImage(imgData);
    } catch (e) {
      console.warn("Image compression failed, using original base64", e);
    }

    const newEntry = {
      id: `rep-${Date.now()}`,
      filename,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
      size: "1.2 MB",
      thumbnail: thumb,
      prediction
    };
    const updated = [newEntry, ...history.slice(0, 2)];
    setHistory(updated);
    try {
      localStorage.setItem("crop_report_history_v3", JSON.stringify(updated));
    } catch (e) {
      console.warn("LocalStorage write failed, resetting key", e);
    }
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Unsupported file format. Please upload a valid image (PNG, JPG, JPEG).");
      return;
    }
    setLoading(true);
    setError(null);

    let localBase64 = "";
    try {
      localBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      setProcessingImage(localBase64);
    } catch (e) {
      console.warn("Base64 reader error", e);
    }

    try {
      const [cropRes, pestRes, stageRes] = await Promise.all([
        predictCrop(file),
        predictPestDisease(file),
        predictStage(file)
      ]);

      let cropPrediction = (cropRes.detections && cropRes.detections[0]) || { label: "Unknown", confidence: 0 };
      let cropLabel = normalizeCropLabel(cropPrediction.label || "undefined");
      let stagePrediction = (stageRes.detections && stageRes.detections[0]) || { label: "Unknown", confidence: 0, timeline: [], activeStageIndex: 0 };
      let stageLabel = normalizePestDiseaseLabel(stagePrediction.label || "undefined");

      let diseaseLabel = normalizePestDiseaseLabel(pestRes.disease ?? "undefined");
      let pestLabel = normalizePestDiseaseLabel(pestRes.pest ?? "undefined");
      let stressLabel = normalizePestDiseaseLabel(pestRes.stress ?? "undefined");
      if (stressLabel.toLowerCase() === "abiotic" && (pestRes.stress_confidence || 0) < 0.5) {
        stressLabel = "None";
      }
      const diseaseConf = pestRes.disease_confidence || 0;
      const pestConf = pestRes.pest_confidence || 0;
      let categoryLabel = pestRes.category || "None";

      if (cropLabel === "undefined" || cropLabel === "unidentified" || cropLabel === "unknown") {
        cropLabel = "undefined";
        diseaseLabel = "None";
        pestLabel = "None";
        stageLabel = "None";
        stressLabel = "None";
        categoryLabel = "None";
      } else {
        if (diseaseConf < 0.65) {
          diseaseLabel = "None";
        }
        if (pestConf < 0.65) {
          pestLabel = "None";
        }
        if (diseaseLabel === "None" && pestLabel === "None") {
          categoryLabel = "None";
        }
        if (stressLabel.toLowerCase() === "abiotic") {
          diseaseLabel = "None";
          pestLabel = "None";
          categoryLabel = `${((pestRes.stress_confidence || 0.0) * 100).toFixed(1)}%`;
        }
      }

      cropPrediction.label = cropLabel;
      stagePrediction.label = stageLabel;

      const pestPrediction = {
        crop: pestRes.crop || "Unknown",
        disease: diseaseLabel,
        pest: pestLabel,
        category: categoryLabel,
        stress: stressLabel,
        stressConfidence: pestRes.stress_confidence || 0.0,
        cropConfidence: pestRes.crop_confidence || 0,
        diseaseConfidence: diseaseConf,
        pestConfidence: pestConf,
        finalLabel: pestRes.final_label || "",
        symptoms: pestRes.symptoms || "",
        diagnosticReasoning: pestRes.diagnostic_reasoning || "",
        recommendations: pestRes.recommendations || "",
        boxes: pestRes.boxes || []
      };

      const prediction = {
        crop: cropPrediction,
        pest: pestPrediction,
        stage: stagePrediction
      };

      const resultEntry = {
        filename: file.name,
        image: localBase64,
        prediction
      };

      setSingleResult(resultEntry);
      await saveToHistory(file.name, prediction, cropRes.image || localBase64);
      setUiState(2);
      resetZoomPan();
    } catch (err) {
      console.warn("Backend predictions failed, falling back to mock preview", err);
      // Fallback mock prediction
      const crops = ["Wheat", "Paddy", "Maize", "Cotton", "Chickpea", "Soybean", "Mustard"];
      const chosenCrop = crops[Math.floor(Math.random() * crops.length)];

      const cropData = CROP_DATA.find(c => c.name.toLowerCase() === chosenCrop.toLowerCase()) || CROP_DATA[0];
      const cropDiseases = cropData.diseases.filter(d => d.name !== "Healthy");
      const hasDisease = Math.random() > 0.3;
      const chosenDisease = hasDisease ? cropDiseases[Math.floor(Math.random() * cropDiseases.length)].name : "Healthy";
      const isHealthy = chosenDisease === "Healthy";
      const mockConf = 0.8 + Math.random() * 0.15;

      const timeline = cropData.stages;
      const activeStageIdx = Math.floor(Math.random() * timeline.length);
      const chosenStage = timeline[activeStageIdx];

      const mockStress = isHealthy ? "None" : (Math.random() > 0.2 ? "Biotic" : "Abiotic");
      const mockDisease = mockStress === "Abiotic" ? "None" : chosenDisease;
      const mockPest = mockStress === "Abiotic" ? "None" : (isHealthy ? "No pest found" : (Math.random() > 0.5 ? "Aphids" : "Brown Plant Hopper"));

      const mockPrediction = {
        crop: {
          label: cropData.name,
          confidence: mockConf
        },
        pest: {
          crop: cropData.name.toUpperCase(),
          disease: mockDisease,
          pest: mockPest,
          stress: mockStress,
          stressConfidence: isHealthy ? 0.0 : 0.75 + Math.random() * 0.15,
          cropConfidence: mockConf,
          diseaseConfidence: isHealthy ? 0.95 : mockConf - 0.05,
          pestConfidence: isHealthy ? 0.95 : 0.8 + Math.random() * 0.15,
          category: isHealthy ? "Healthy" : (mockStress === "Abiotic" ? "Abiotic" : (Math.random() > 0.5 ? "Pest" : "Disease")),
          finalLabel: isHealthy ? `${cropData.name}-Healthy` : `${cropData.name}-${chosenDisease}`,
          symptoms: isHealthy
            ? `${cropData.name} foliage appears healthy with no visible symptoms.`
            : `${cropData.name} leaves displaying lesions, discoloration, and spots consistent with ${chosenDisease}.`,
          diagnosticReasoning: isHealthy
            ? "No pathological signs observed."
            : `Foliage analysis shows visual patterns typical of ${chosenDisease} in ${cropData.name}.`,
          recommendations: isHealthy
            ? "Treatment: No treatment required at this time.\n\nPrevention: Maintain standard crop monitoring and sanitation."
            : `Treatment: Apply recommended treatment. Monitor humidity.\n\nPrevention: Clean tools and remove infected plant debris.`,
          boxes: isHealthy ? [] : [{ label: chosenDisease, confidence: 0.9, x: 25, y: 35, w: 20, h: 20 }]
        },
        stage: {
          label: chosenStage,
          confidence: 0.85 + Math.random() * 0.1,
          timeline: timeline,
          activeStageIndex: activeStageIdx,
          description: `The crop is in the ${chosenStage.toLowerCase()} stage. Maintain watering and nutrition.`
        }
      };

      const resultEntry = {
        filename: file.name,
        image: localBase64,
        prediction: mockPrediction
      };

      setSingleResult(resultEntry);
      await saveToHistory(file.name, mockPrediction, localBase64);
      setUiState(2);
      resetZoomPan();
    } finally {
      setLoading(false);
      setProcessingImage(null);
    }
  };

  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) {
      alert("No image files found in selected folder.");
      return;
    }
    setProcessedResults([]);
    setActiveIndex(0);
    setUiState(3);

    try {
      const base64Str = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(files[0]);
      });
      setFolderLoadingImage(base64Str);
    } catch (err) {
      console.warn("Folder loading image preview failed", err);
    }

    startFolderProcessing(files);
  };

  const startFolderProcessing = async (files) => {
    setIsBatchProcessing(true);
    setBatchProgress(0);
    const resultsList = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const [cropRes, pestRes, stageRes] = await Promise.all([
          predictCrop(files[i]),
          predictPestDisease(files[i]),
          predictStage(files[i])
        ]);

        let cropPrediction = (cropRes.detections && cropRes.detections[0]) || { label: "Unknown", confidence: 0 };
        let cropLabel = normalizeCropLabel(cropPrediction.label || "undefined");
        let stagePrediction = (stageRes.detections && stageRes.detections[0]) || { label: "Unknown", confidence: 0, timeline: [], activeStageIndex: 0 };
        let stageLabel = normalizePestDiseaseLabel(stagePrediction.label || "undefined");

        let diseaseLabel = normalizePestDiseaseLabel(pestRes.disease ?? "undefined");
        let pestLabel = normalizePestDiseaseLabel(pestRes.pest ?? "undefined");
        let stressLabel = normalizePestDiseaseLabel(pestRes.stress ?? "undefined");
        if (stressLabel.toLowerCase() === "abiotic" && (pestRes.stress_confidence || 0) < 0.5) {
          stressLabel = "None";
        }
        const diseaseConf = pestRes.disease_confidence || 0;
        const pestConf = pestRes.pest_confidence || 0;
        let categoryLabel = pestRes.category || "None";

        if (cropLabel === "undefined" || cropLabel === "unidentified" || cropLabel === "unknown") {
          cropLabel = "undefined";
          diseaseLabel = "None";
          pestLabel = "None";
          stageLabel = "None";
          stressLabel = "None";
          categoryLabel = "None";
        } else {
          if (diseaseConf < 0.65) {
            diseaseLabel = "None";
          }
          if (pestConf < 0.65) {
            pestLabel = "None";
          }
          if (diseaseLabel === "None" && pestLabel === "None") {
            categoryLabel = "None";
          }
          if (stressLabel.toLowerCase() === "abiotic") {
            diseaseLabel = "None";
            pestLabel = "None";
            categoryLabel = `${((pestRes.stress_confidence || 0.0) * 100).toFixed(1)}%`;
          }
        }

        cropPrediction.label = cropLabel;
        stagePrediction.label = stageLabel;

        const pestPrediction = {
          crop: pestRes.crop || "Unknown",
          disease: diseaseLabel,
          pest: pestLabel,
          category: categoryLabel,
          stress: stressLabel,
          stressConfidence: pestRes.stress_confidence || 0.0,
          cropConfidence: pestRes.crop_confidence || 0,
          diseaseConfidence: diseaseConf,
          pestConfidence: pestConf,
          finalLabel: pestRes.final_label || "",
          symptoms: pestRes.symptoms || "",
          diagnosticReasoning: pestRes.diagnostic_reasoning || "",
          recommendations: pestRes.recommendations || "",
          boxes: pestRes.boxes || []
        };

        let localFileBase64 = "";
        try {
          localFileBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(files[i]);
          });
        } catch (e) { }

        resultsList.push({
          filename: files[i].name,
          image: localFileBase64 || cropRes.image,
          prediction: {
            crop: cropPrediction,
            pest: pestPrediction,
            stage: stagePrediction
          },
          status: "Completed"
        });
      } catch (err) {
        let failBase64 = "https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=400";
        try {
          failBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(files[i]);
          });
        } catch (_) { }

        // Fallback folder entry mock values
        resultsList.push({
          filename: files[i].name,
          image: failBase64,
          prediction: {
            crop: { label: "Wheat", confidence: 0.82 },
            pest: {
              crop: "WHEAT",
              disease: "Healthy",
              pest: "No pest found",
              stress: "None",
              stressConfidence: 0.0,
              cropConfidence: 0.82,
              diseaseConfidence: 0.84,
              pestConfidence: 0.95,
              category: "Healthy",
              finalLabel: "Wheat-Healthy",
              symptoms: "Wheat foliage appears healthy.",
              diagnosticReasoning: "",
              recommendations: "Treatment: None required.\n\nPrevention: Standard care.",
              boxes: []
            },
            stage: { label: "Maturity", confidence: 0.80, timeline: ["Seedling", "Vegetative Growth", "Reproductive Flowering", "Grain Filling", "Maturity"], activeStageIndex: 4 }
          },
          status: "Failed"
        });
      }
      setProcessedResults([...resultsList]);
      setBatchProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setIsBatchProcessing(false);
    setFolderLoadingImage(null);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleUploadImage(file);
    }
  };

  const handleHeaderBack = () => {
    clearSession();
    if (uiState !== 1 && uiState !== 4) {
      setUiState(1);
    } else {
      onBack();
    }
  };

  // Zoom & Pan Event Handlers
  const handleMouseDown = (e) => {
    if (!isPanActive) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPos.x, y: e.clientY - panPos.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPanPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => { setIsDragging(false); };
  const handleImageWheel = (e) => {
    const isZoomGesture = e.ctrlKey || e.metaKey || e.altKey;
    if (!isZoomGesture) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.15 : -0.15;
    setZoomScale(prev => Math.min(4, Math.max(0.5, Number((prev + delta).toFixed(2)))));
  };
  const resetZoomPan = () => {
    setZoomScale(1);
    setPanPos({ x: 0, y: 0 });
    setIsPanActive(false);
  };

  // Render markdown bold text
  const renderMarkdownText = (text) => {
    if (!text) return "";
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={index} className="font-semibold text-on-surface">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const renderFormattedPoints = (content, textSizeClass = "text-sm") => {
    if (!content) return null;
    const lines = content.split("\n").map(l => l.trim()).filter(Boolean);
    return (
      <ul className="space-y-2 mt-1.5">
        {lines.map((line, idx) => (
          <li key={idx} className={`flex items-start gap-2 ${textSizeClass} text-on-surface-variant leading-relaxed`}>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-on-surface mt-2 shrink-0"></span>
            <div className="flex-1">{renderMarkdownText(line)}</div>
          </li>
        ))}
      </ul>
    );
  };

  const activeRow = processedResults[activeIndex];

  const filteredCrops = CROP_DATA.filter(crop =>
    crop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    crop.diseases.some(d => d.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="h-full flex flex-col w-full relative bg-surface overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 w-full z-40 bg-surface/90 dark:bg-inverse-surface/90 backdrop-blur-md border-b border-outline-variant/30 flex justify-between items-center px-4 sm:px-margin-desktop h-16 shadow-sm shrink-0">
        <div className="flex items-center gap-2 sm:gap-stack-md">
          <button
            onClick={handleHeaderBack}
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-surface-container-low hover:bg-surface-container border border-outline-variant/30 text-on-surface-variant hover:text-primary transition-all duration-200 shrink-0 group text-xs sm:text-sm font-semibold shadow-xs"
            title="Return to previous screen"
          >
            <span className="material-symbols-outlined text-[18px] sm:text-[20px] group-hover:-translate-x-0.5 transition-transform duration-200">arrow_back</span>
            <span className="truncate max-w-[80px] sm:max-w-none">{uiState === 1 ? "Dashboard" : "Back"}</span>
          </button>
          <div className="h-6 w-px bg-outline-variant/50 mx-1 sm:mx-2 shrink-0"></div>

          {/* Toggle Catalog/Report mode */}
          {/* <div className="flex items-center gap-2">
            <button
              onClick={() => setUiState(1)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all ${uiState !== 4 ? "bg-primary text-on-primary border-primary" : "bg-transparent border-outline text-on-surface-variant hover:bg-surface-container"}`}
            >
              Prediction Mode
            </button>
            <button
              onClick={() => setUiState(4)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all ${uiState === 4 ? "bg-primary text-on-primary border-primary" : "bg-transparent border-outline text-on-surface-variant hover:bg-surface-container"}`}
            >
              Crop Catalog
            </button>
          </div> */}
        </div>

        <div className="flex items-center gap-stack-md">
          <button className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full">
            <span className="material-symbols-outlined">search</span>
          </button>
          <button
            onClick={() => setShowVerifiedModal(true)}
            className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full relative"
            title="Verified Reports"
          >
            <span className="material-symbols-outlined">notifications</span>
            {(() => {
              const seenTickets = JSON.parse(localStorage.getItem("seen_tickets") || "[]");
              const unreadCount = userTickets.filter((t) => t.status === "Resolved" && !seenTickets.includes(t.ticket_id)).length;
              return unreadCount > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-error text-white text-[10px] font-extrabold rounded-full ring-2 ring-surface flex items-center justify-center">
                  {unreadCount}
                </span>
              ) : null;
            })()}
          </button>

          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-outline-variant/30 overflow-hidden bg-white hover:border-primary transition-all duration-250 p-1 shadow-sm hover:scale-105"
            >
              <img
                src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                alt="Niruthi Logo"
                className="w-full h-full object-contain"
              />
            </button>
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-inverse-surface border border-outline-variant/30 rounded-xl shadow-lg z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-outline-variant/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border border-outline-variant/30 overflow-hidden bg-white shrink-0 p-0.5 shadow-sm">
                    <img
                      src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                      alt="User Profile"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-on-surface truncate">Niruthi User</p>
                    <p className="text-xs text-on-surface-variant truncate">Niruthiuser</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 flex items-center gap-2 transition-colors font-medium"
                >
                  <span className="material-symbols-outlined text-[18px]">logout</span>
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* STATE 1: Upload Layout */}
      {uiState === 1 && (
        <div className="px-6 py-4 max-w-7xl mx-auto w-full flex-1 flex flex-col gap-stack-lg overflow-y-auto hide-scrollbar pb-16">
          <div>
            <h3 className="font-headline-lg text-headline-lg text-on-background mb-2">Crop Report</h3>
            <p className="font-body-lg text-body-lg text-on-surface-variant">Identify crop type, disease, pest instances and plant growth stage simultaneously.</p>
          </div>

          {/* Drag & Drop Area / Scanning Overlay */}
          {loading && processingImage ? (
            <div className="w-full h-[380px] rounded-20 border border-outline-variant/30 overflow-hidden relative shadow-lg">
              <img
                src={processingImage}
                alt="Processing preview"
                className="w-full h-full object-cover filter brightness-[0.7]"
              />
              <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-0 text-white gap-3">
                <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Performing Multi-model Predictions...</p>
                <p className="font-body-sm text-body-sm text-white/80 drop-shadow-sm">Identifying Crop, Pests, Diseases & Stage</p>
              </div>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`drop-zone w-full rounded-20 border-2 border-dashed border-outline-variant/60 bg-surface-container-low hover:border-primary-container hover:bg-primary-container/5 flex flex-col items-center justify-center py-4 px-6 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] cursor-pointer ${isDragOver ? "dragover" : ""}`}
            >
              <div className="w-11 h-11 rounded-full bg-surface shadow-sm flex items-center justify-center mb-2">
                <span className="material-symbols-outlined text-primary text-[22px]">{loading ? "sync" : "assignment"}</span>
              </div>
              <h4 className="font-headline-md text-headline-md text-on-background mb-0.5 text-center">
                {loading ? "Analyzing imagery..." : "Drag and drop crop imagery here"}
              </h4>
              <p className="font-body-sm text-body-sm text-on-surface-variant mb-4 text-center">or browse local crop files.</p>

              <div className="flex items-center gap-stack-md mb-3">
                <label className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-on-primary rounded-lg font-body-sm font-semibold shadow-md hover:shadow-lg transition-all duration-250 flex items-center gap-2 cursor-pointer">
                  <span className="material-symbols-outlined text-[18px]">image</span> Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleUploadImage(e.target.files[0])}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => anotherFolderInputRef.current?.click()}
                  className="px-6 py-2.5 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all duration-250 rounded-lg font-body-sm font-medium flex items-center gap-2 cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[18px]">folder</span> Upload Folder
                </button>
              </div>
              <p className="font-label-code text-label-code text-tertiary">Supported formats: JPG, PNG, TIFF, WEBP</p>
              {error && <p className="text-error font-body-sm mt-4">{error}</p>}
            </div>
          )}

          {/* History */}
          <div className="-mt-4">
            <h4 className="font-headline-md text-headline-md text-on-background mb-3">Recent Activity</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-stack-md">
              {history.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    const resultEntry = {
                      filename: item.filename,
                      image: item.thumbnail,
                      prediction: normalizePrediction(item.prediction)
                    };
                    setSingleResult(resultEntry);
                    setUiState(2);
                    resetZoomPan();
                  }}
                  className="bg-surface-container-lowest rounded-xl p-3 shadow-[0px_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0px_8px_24px_rgba(0,0,0,0.08)] border border-transparent transition-all duration-250 flex flex-col group cursor-pointer"
                >
                  <div className="w-full h-52 rounded-lg overflow-hidden mb-4 relative bg-surface-container">
                    <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" src={getImageUrl(item.thumbnail)} alt={item.filename} />
                    <div className="absolute top-2 right-2 bg-primary-container/90 text-on-primary px-2 py-0.5 rounded-full font-label-code text-[10px] backdrop-blur-sm">Processed</div>
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h5 className="font-body-sm text-body-sm font-semibold text-on-background truncate">{item.filename}</h5>
                      <p className="font-label-code text-label-code text-on-surface-variant mt-1">{item.date}</p>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-[12px] font-medium text-secondary truncate">
                        <span className="material-symbols-outlined text-[15px] text-primary">spa</span>
                        <span className="truncate">{item.prediction.crop.label}</span>
                        <span className="text-on-surface-variant/40">•</span>
                        <span className="truncate text-on-surface-variant">{item.prediction.stage.label}</span>
                      </div>
                      <button className="p-1.5 rounded-md text-tertiary hover:bg-surface-container hover:text-primary transition-colors duration-250 shrink-0">
                        <span className="material-symbols-outlined text-[18px]">open_in_new</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STATE 2: Single Result Layout */}
      {uiState === 2 && singleResult && (
        <div className="flex-1 overflow-y-auto p-4 sm:p-margin-desktop flex flex-col min-h-0 pb-16 lg:pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-gutter lg:h-full lg:min-h-[500px]">
            {/* Left Panel */}
            <div className="flex flex-col bg-surface-container-lowest rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-outline-variant/30 overflow-hidden relative group h-[280px] sm:h-[360px] lg:h-full lg:min-h-0 shrink-0">
              {/* Quick mobile prediction badge */}
              <div className="absolute top-3 left-3 bg-surface/90 backdrop-blur-md px-3 py-1.5 rounded-lg shadow-sm border border-outline-variant/30 flex items-center gap-1.5 lg:hidden z-10 max-w-[85%]">
                <span className="material-symbols-outlined text-primary text-[16px]">assessment</span>
                <span className="font-bold text-xs text-on-surface truncate">
                  {singleResult.prediction?.crop?.label || "Report Analysis"}
                </span>
              </div>

              {loading && processingImage ? (
                <div className="w-full h-full overflow-hidden relative flex-1 flex flex-col items-center justify-center bg-surface-container-low">
                  <img
                    src={processingImage}
                    alt="Scanning preview"
                    className="max-h-full max-w-full object-contain filter brightness-[0.7]"
                  />
                  <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-0 text-white gap-3">
                    <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                    <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Scanning Image...</p>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleImageWheel}
                    className="flex-1 relative bg-surface-container-low overflow-auto flex items-center justify-center select-none min-h-0"
                    style={{ cursor: isPanActive ? "grab" : "default" }}
                  >
                    {/* Plotted Bounding Boxes */}
                    <div
                      className="relative transition-transform w-full h-full flex items-center justify-center"
                      style={{
                        transform: `translate(${panPos.x}px, ${panPos.y}px) scale(${zoomScale})`,
                        transformOrigin: "center center",
                        cursor: isPanActive ? "grab" : "default"
                      }}
                    >
                      <img
                        src={getImageUrl(singleResult.image)}
                        alt="Crop report preview"
                        className="max-h-full max-w-full object-contain"
                      />
                      {/* Bounding box divs overlays */}
                      {(singleResult.prediction?.pest?.boxes || []).map((box, idx) => (
                        <div
                          key={idx}
                          className="absolute border-2 border-error bg-error/10 hover:bg-error/20 transition-all group/box cursor-pointer"
                          style={{
                            left: `${box.x}%`,
                            top: `${box.y}%`,
                            width: `${box.w}%`,
                            height: `${box.h}%`
                          }}
                        >
                          <span className="absolute -top-6 left-[-2px] bg-error text-on-error font-label-code text-[9px] px-1 py-0.5 rounded-t-[2px] whitespace-nowrap shadow-sm opacity-0 group-hover/box:opacity-100 transition-opacity">
                            {box.label} {(box.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-1 sm:gap-2 bg-surface/85 backdrop-blur-md px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-lg border border-outline-variant/30 scale-90 sm:scale-100 origin-bottom">
                    <button onClick={() => setZoomScale(prev => Math.min(prev + 0.25, 4))} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Zoom In"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">zoom_in</span></button>
                    <button onClick={() => setZoomScale(prev => Math.max(prev - 0.25, 0.5))} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Zoom Out"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">zoom_out</span></button>
                    <div className="w-px h-5 sm:h-6 bg-outline-variant/50 mx-0.5 sm:mx-1"></div>
                    <button onClick={() => setIsFullscreen(true)} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Fullscreen"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">fullscreen</span></button>
                    <button onClick={resetZoomPan} className="p-1.5 sm:p-2 text-on-surface hover:text-primary rounded-full hover:bg-surface-variant/50" title="Reset Orientation"><span className="material-symbols-outlined text-[18px] sm:text-[20px]">restart_alt</span></button>
                  </div>
                </>
              )}
            </div>

            {/* Right Information Panel */}
            <div className="flex flex-col gap-4 lg:gap-stack-lg lg:justify-between lg:min-h-0 lg:overflow-hidden lg:h-full">
              <div className="flex flex-col gap-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1 pb-2">
                <div>
                  <div className="flex items-center justify-between">
                    <h2 className="font-headline-lg text-headline-lg text-on-surface">Crop Report Analysis</h2>
                    {singleResult?.isFlagged && (
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1 ${singleResult.status === "Resolved"
                          ? "bg-secondary/15 text-secondary border border-secondary/30"
                          : "bg-surface-container-highest text-on-surface-variant border border-outline-variant/30"
                        }`}>
                        <span className="material-symbols-outlined text-[14px]">
                          {singleResult.status === "Resolved" ? "verified" : "pending"}
                        </span>
                        {singleResult.status === "Resolved" ? "Agronomist Verified" : "Verification Pending"}
                      </span>
                    )}
                  </div>
                  <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">File: {singleResult.filename}</p>
                </div>

                <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
                  {/* Card 1: Identified Crop */}
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Identified Crop</p>
                      <p className="font-headline-md text-headline-md text-on-surface font-semibold mt-1 truncate" title={singleResult.prediction.crop.label}>
                        {singleResult.prediction.crop.label}
                      </p>
                    </div>
                    <span className="text-xs text-primary font-mono mt-1 block">
                      Conf: {((singleResult.prediction.crop.confidence || 0.0) * 100).toFixed(1)}%
                    </span>
                  </div>

                  {/* Card 2: Lifecycle Stage */}
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between" title={singleResult.prediction.stage.label}>
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Lifecycle Stage</p>
                      <p className="font-headline-md text-headline-md text-on-surface font-semibold mt-1 truncate" title={singleResult.prediction.stage.label}>
                        {singleResult.prediction.stage.label}
                      </p>
                    </div>
                    <span className="text-xs text-secondary font-mono mt-1 block">
                      Conf: {((singleResult.prediction.stage.confidence || 0.0) * 100).toFixed(1)}%
                    </span>
                  </div>

                  {/* Card 3: Disease Status */}
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Disease Status</p>
                      <p className={`font-headline-md text-headline-md font-semibold mt-1 truncate ${singleResult.prediction.pest.disease && singleResult.prediction.pest.disease.toLowerCase() !== "healthy" && singleResult.prediction.pest.disease.toLowerCase() !== "none" ? "text-error" : "text-on-surface"}`} title={singleResult.prediction.pest.disease}>
                        {singleResult.prediction.pest.disease}
                      </p>
                    </div>
                    {singleResult.prediction.pest.disease &&
                      singleResult.prediction.pest.disease.toLowerCase() !== "none" &&
                      singleResult.prediction.pest.disease.toLowerCase() !== "healthy" &&
                      singleResult.prediction.pest.disease.toLowerCase() !== "no disease found" && (
                        <span className="text-xs text-primary font-mono mt-1 block">
                          Conf: {((singleResult.prediction.pest.diseaseConfidence || 0.0) * 100).toFixed(1)}%
                        </span>
                      )}
                  </div>

                  {/* Card 4: Pest Status */}
                  <div className="bg-surface-container-lowest p-3.5 sm:p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Pest Status</p>
                      <p className={`font-headline-md text-headline-md font-semibold mt-1 truncate ${singleResult.prediction.pest.pest && singleResult.prediction.pest.pest.toLowerCase() !== "no pest found" && singleResult.prediction.pest.pest.toLowerCase() !== "none" ? "text-error" : "text-on-surface"}`} title={singleResult.prediction.pest.pest || "None"}>
                        {singleResult.prediction.pest.pest || "None"}
                      </p>
                    </div>
                    {singleResult.prediction.pest.pest &&
                      singleResult.prediction.pest.pest.toLowerCase() !== "none" &&
                      singleResult.prediction.pest.pest.toLowerCase() !== "no pest found" && (
                        <span className="text-xs text-primary font-mono mt-1 block">
                          Conf: {((singleResult.prediction.pest.pestConfidence || 0.0) * 100).toFixed(1)}%
                        </span>
                      )}
                  </div>

                  {/* Card 5: Stress Status */}
                  <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                    <div>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase">Stress Status</p>
                      <p className={`font-headline-md text-headline-md font-semibold mt-1 truncate ${singleResult.prediction.pest.stress && singleResult.prediction.pest.stress.toLowerCase() === "abiotic"
                        ? "text-amber-600"
                        : singleResult.prediction.pest.stress && singleResult.prediction.pest.stress.toLowerCase() === "biotic"
                          ? "text-error"
                          : "text-on-surface-variant"
                        }`}>
                        {singleResult.prediction.pest.stress || "None"}
                      </p>
                    </div>
                    {singleResult.prediction.pest.stress &&
                      singleResult.prediction.pest.stress.toLowerCase() !== "none" &&
                      singleResult.prediction.pest.stress.toLowerCase() !== "unidentified" && (
                        <span className="text-xs text-tertiary font-mono mt-1 block">
                          Conf: {((singleResult.prediction.pest.stressConfidence || 0.0) * 100).toFixed(1)}%
                        </span>
                      )}
                  </div>

                  {/* Card 6: Category OR Stress Confidence */}
                  {singleResult.prediction.pest.stress && singleResult.prediction.pest.stress.toLowerCase() === "abiotic" ? (
                    <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                      <div>
                        <p className="font-label-code text-label-code text-on-surface-variant uppercase">Stress Confidence</p>
                        <p className="font-headline-md text-headline-md text-primary font-semibold mt-1">
                          {((singleResult.prediction.pest.stressConfidence || 0.0) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30 flex flex-col justify-between">
                      <div>
                        <p className="font-label-code text-label-code text-on-surface-variant uppercase">Category</p>
                        <p className={`font-headline-md text-headline-md font-semibold mt-1 ${singleResult.prediction.pest.category === "None"
                            ? "text-on-surface-variant"
                            : singleResult.prediction.pest.category === "Healthy"
                              ? "text-secondary"
                              : "text-error"
                          }`}>
                          {singleResult.prediction.pest.category || "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Symptoms description */}
                {singleResult.prediction.pest.symptoms && (
                  <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30">
                    <div className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-amber-500 mt-0.5">info</span>
                      <div>
                        <h3 className="font-body-lg font-semibold text-on-surface mb-1">Identified Symptoms</h3>
                        <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">
                          {singleResult.prediction.pest.symptoms}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Growth Stage Timeline */}
                {singleResult.prediction.stage && (singleResult.prediction.stage.timeline || []).length > 0 && (
                  <div className="bg-surface-container-lowest p-4 rounded-xl border border-outline-variant/30 flex flex-col gap-2">
                    <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Growth Stage Timeline</p>
                    <div className="flex justify-between items-start relative z-10 pt-3 pb-1">
                      {/* Line connecting nodes */}
                      <div className="absolute top-[22px] left-0 w-full h-[2px] bg-surface-container-high -z-10 -translate-y-1/2"></div>
                      {/* Nodes */}
                      {singleResult.prediction.stage.timeline.map((stg, i) => {
                        const hasData = singleResult.prediction.stage.label && singleResult.prediction.stage.label.toLowerCase() !== "unknown" && singleResult.prediction.stage.label.toLowerCase() !== "none";
                        const isActive = hasData && (singleResult.prediction.stage.activeStageIndex === i || singleResult.prediction.stage.label.toLowerCase() === stg.toLowerCase());
                        const isPassed = hasData && (singleResult.prediction.stage.activeStageIndex > i);

                        return (
                          <div key={i} className="flex flex-col items-center gap-1.5 relative">
                            <div className={`w-4.5 h-4.5 rounded-full flex items-center justify-center ring-4 ring-surface-container-lowest z-10 relative ${isActive ? "bg-primary border-2 border-surface-container-lowest text-on-primary" : isPassed ? "bg-primary text-on-primary" : "bg-surface-container-high border-2 border-outline-variant"}`}>
                              {isActive && <div className="absolute -inset-1 rounded-full bg-primary/45 timeline-pulse"></div>}
                              {isPassed && <span className="material-symbols-outlined text-[10px] font-bold">check</span>}
                              {isActive && <div className="w-1.5 h-1.5 bg-on-primary rounded-full relative z-10"></div>}
                            </div>
                            <span className={`font-label-code text-[10px] ${isActive ? "text-primary font-bold" : "text-on-surface-variant"}`}>{stg}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                <div className="flex flex-col min-h-0">
                  <div className="flex items-start gap-3 min-h-0">
                    <span className="material-symbols-outlined text-primary-container mt-0.5">prescriptions</span>
                    <div className="flex-1 min-w-0 min-h-0">
                      <h3 className="font-body-lg font-semibold text-on-surface mb-2">Actions Recommended</h3>
                      <div className="treatment-scroll space-y-4 overflow-y-auto pr-2" style={{ maxHeight: "min(24vh, 220px)" }}>
                        {singleResult.prediction.pest.recommendations && singleResult.prediction.pest.recommendations.trim() ? (
                          singleResult.prediction.pest.recommendations.split("\n\n").map((para, i) => {
                            const isTreatment = para.startsWith("Treatment:");
                            const isPrevention = para.startsWith("Prevention:");
                            let content = para;
                            let label = "";
                            if (isTreatment) {
                              label = "Treatment Plan:";
                              content = para.replace("Treatment:", "").trim();
                            } else if (isPrevention) {
                              label = "Preventive Precautions:";
                              content = para.replace("Prevention:", "").trim();
                            }
                            return (
                              <div key={i} className="mb-4 last:mb-0">
                                {label && <span className="font-bold text-on-surface text-sm block mb-1">{label}</span>}
                                {renderFormattedPoints(content, "text-sm")}
                              </div>
                            );
                          })
                        ) : (
                          <p className="font-sans text-sm text-on-surface-variant leading-relaxed">No treatments recommended</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2.5 mt-4 lg:mt-auto lg:shrink-0 w-full pt-3 border-t border-outline-variant/20 lg:border-t-0 lg:pt-0">
                <button
                  onClick={handleFlagVerification}
                  disabled={verificationSent || loading}
                  className={`w-full flex items-center justify-center gap-2 font-body-sm text-body-sm py-2.5 px-4 rounded-lg transition-all border ${verificationSent
                      ? "bg-primary/10 text-primary border-primary/20 cursor-default"
                      : "bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 cursor-pointer"
                    }`}
                >
                  <span className="material-symbols-outlined text-[18px]">
                    {verificationSent ? "check_circle" : "flag"}
                  </span>
                  <span className="truncate">{verificationSent ? "Flagged" : "Flag for Verification"}</span>
                </button>
                <button
                  onClick={() => anotherInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-on-primary font-body-sm text-body-sm py-2.5 px-4 rounded-lg hover:bg-primary/95 transition-all shadow-sm"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined text-[18px]">add_a_photo</span>
                  <span className="truncate">Predict Another Image</span>
                </button>
                <button
                  onClick={() => anotherFolderInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all py-2.5 px-4 rounded-lg font-body-sm text-body-sm"
                  disabled={loading}
                >
                  <span className="material-symbols-outlined text-[18px]">folder</span>
                  <span className="truncate">Predict Another Folder</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* STATE 3: Folder Batch Processing Layout */}
      {uiState === 3 && (
        (processedResults.length === 0 && isBatchProcessing) ? (
          <div className="flex-1 flex flex-col justify-center items-center p-margin-desktop bg-surface min-h-[500px]">
            <div className="w-full max-w-2xl rounded-xl border border-outline-variant/30 overflow-hidden relative shadow-lg h-[400px]">
              {folderLoadingImage ? (
                <img
                  src={folderLoadingImage}
                  alt="Folder upload preview"
                  className="w-full h-full object-cover filter brightness-[0.6] blur-[2px]"
                />
              ) : (
                <div className="w-full h-full bg-surface-container-low"></div>
              )}
              <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 z-0 text-white p-6 text-center gap-4">
                <div className="w-16 h-16 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                <div>
                  <h3 className="font-headline-lg text-headline-lg font-bold tracking-wide mb-2 drop-shadow-md">Batch Folder Analysis Started</h3>
                  <p className="font-body-md text-body-md text-white/80 max-w-md mx-auto leading-relaxed drop-shadow-sm">
                    We are analyzing imagery queueing for deep learning batch inference across multiple diagnostic modules.
                  </p>
                </div>
                <div className="w-64 bg-white/20 h-2 rounded-full overflow-hidden mt-4">
                  <div className="bg-primary h-full transition-all duration-300 animate-pulse" style={{ width: "20%" }}></div>
                </div>
                <span className="font-label-code text-[11px] text-white/60 tracking-wider">Please keep this browser window active</span>
              </div>
            </div>
          </div>
        ) : activeRow ? (
          <div className="flex-1 overflow-y-auto hide-scrollbar px-4 md:px-6 pt-4 pb-20 lg:pb-4 space-y-4">
            {isBatchProcessing && (
              <div className="shrink-0 w-full bg-surface-container-lowest p-3 rounded-xl border border-outline-variant/20 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex justify-between text-body-sm text-on-surface-variant mb-1 font-semibold">
                    <span>Batch report analysis active</span>
                    <span>{batchProgress}% ({processedResults.length} files)</span>
                  </div>
                  <div className="w-full bg-surface-container-high h-2 rounded-full overflow-hidden">
                    <div className="bg-primary h-full transition-all duration-300" style={{ width: `${batchProgress}%` }}></div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-gutter min-h-0 h-auto pb-6 lg:pb-0">
              <div className="lg:col-span-5 bg-surface-container-lowest rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.04)] border border-outline-variant/20 overflow-hidden relative h-[260px] sm:h-[320px] lg:h-[380px] flex items-center justify-center bg-surface-container-low shrink-0">
                <div className="relative w-full h-full flex items-center justify-center">
                  <img src={getImageUrl(activeRow.image)} alt="Active report frame" className="max-h-full max-w-full object-contain" />
                  {(activeRow.prediction?.pest?.boxes || []).map((box, idx) => (
                    <div
                      key={idx}
                      className="absolute border-2 border-error bg-error/10 hover:bg-error/20 transition-all cursor-pointer"
                      style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.w}%`, height: `${box.h}%` }}
                    />
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7 bg-surface-container-lowest rounded-xl p-4 sm:p-6 border border-outline-variant/20 flex flex-col justify-between min-h-0 lg:min-h-[440px] pb-4 lg:overflow-hidden overflow-visible">
                <div>
                  <h3 className="font-headline-md text-headline-md text-on-surface mb-2">Detection Details</h3>
                  {/* <p className="font-body-sm text-body-sm text-on-surface-variant mt-1">Status: <span className="font-semibold text-secondary">{activeRow.status}</span></p> */}

                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                    <div className="bg-surface-container-low p-3 rounded-lg">
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Crop</p>
                      <p className="font-body-sm text-primary font-semibold mt-1 truncate">{activeRow.prediction.crop.label}</p>
                      {activeRow.prediction.crop.confidence > 0 && (
                        <p className="text-[11px] text-primary font-mono mt-1 font-medium">
                          Conf: {((activeRow.prediction.crop.confidence || 0) * 100).toFixed(1)}%
                        </p>
                      )}
                    </div>
                    <div className="bg-surface-container-low p-3 rounded-lg" title={activeRow.prediction.stage.label}>
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Stage</p>
                      <p className="font-body-sm text-secondary font-semibold mt-1 truncate" title={activeRow.prediction.stage.label}>{activeRow.prediction.stage.label}</p>
                      {activeRow.prediction.stage.confidence > 0 && (
                        <p className="text-[11px] text-secondary font-mono mt-1 font-medium">
                          Conf: {((activeRow.prediction.stage.confidence || 0) * 100).toFixed(1)}%
                        </p>
                      )}
                    </div>
                    <div className="bg-surface-container-low p-3 rounded-lg">
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Disease</p>
                      <p className="font-body-sm text-error font-semibold mt-1 truncate" title={activeRow.prediction.pest.disease}>{activeRow.prediction.pest.disease}</p>
                      {activeRow.prediction.pest.disease &&
                        activeRow.prediction.pest.disease.toLowerCase() !== "none" &&
                        activeRow.prediction.pest.disease.toLowerCase() !== "healthy" &&
                        activeRow.prediction.pest.disease.toLowerCase() !== "no disease found" && (
                          <p className="text-[11px] text-primary font-mono mt-1 font-medium">
                            Conf: {((activeRow.prediction.pest.diseaseConfidence || 0.0) * 100).toFixed(0)}%
                          </p>
                        )}
                    </div>
                    <div className="bg-surface-container-low p-3 rounded-lg">
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Pest</p>
                      <p className="font-body-sm text-error font-semibold mt-1 truncate" title={activeRow.prediction.pest.pest || "No pest found"}>{activeRow.prediction.pest.pest || "No pest found"}</p>
                      {activeRow.prediction.pest.pest &&
                        activeRow.prediction.pest.pest.toLowerCase() !== "none" &&
                        activeRow.prediction.pest.pest.toLowerCase() !== "no pest found" && (
                          <p className="text-[11px] text-primary font-mono mt-1 font-medium">
                            Conf: {((activeRow.prediction.pest.pestConfidence || 0.0) * 100).toFixed(0)}%
                          </p>
                        )}
                    </div>
                    <div className="bg-surface-container-low p-3 rounded-lg">
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold">Stress</p>
                      <p className={`font-body-sm font-semibold mt-1 truncate ${activeRow.prediction.pest.stress && activeRow.prediction.pest.stress.toLowerCase() === "abiotic" ? "text-amber-600" : activeRow.prediction.pest.stress && activeRow.prediction.pest.stress.toLowerCase() === "biotic" ? "text-error" : "text-on-surface-variant"}`}>
                        {activeRow.prediction.pest.stress || "None"}
                      </p>
                      {activeRow.prediction.pest.stress &&
                        activeRow.prediction.pest.stress.toLowerCase() !== "none" &&
                        activeRow.prediction.pest.stress.toLowerCase() !== "unidentified" && (
                          <p className="text-[11px] text-primary font-mono mt-1 font-medium">
                            Conf: {((activeRow.prediction.pest.stressConfidence || 0.0) * 100).toFixed(0)}%
                          </p>
                        )}
                    </div>
                  </div>

                  {/* Growth Stage Timeline */}
                  {activeRow.prediction.stage && (activeRow.prediction.stage.timeline || []).length > 0 && (
                    <div className="bg-surface-container-low p-3 rounded-lg border border-outline-variant/20 flex flex-col gap-2 mt-4">
                      <p className="font-label-code text-label-code text-on-surface-variant uppercase font-semibold text-[10px]">Growth Stage Timeline</p>
                      <div className="flex justify-between items-start relative z-10 pt-2 pb-1">
                        {/* Line connecting nodes */}
                        <div className="absolute top-[18px] left-0 w-full h-[1.5px] bg-surface-container-high -z-10 -translate-y-1/2"></div>
                        {/* Nodes */}
                        {activeRow.prediction.stage.timeline.map((stg, i) => {
                          const hasData = activeRow.prediction.stage.label && activeRow.prediction.stage.label.toLowerCase() !== "unknown" && activeRow.prediction.stage.label.toLowerCase() !== "none";
                          const isActive = hasData && (activeRow.prediction.stage.activeStageIndex === i || activeRow.prediction.stage.label.toLowerCase() === stg.toLowerCase());
                          const isPassed = hasData && (activeRow.prediction.stage.activeStageIndex > i);

                          return (
                            <div key={i} className="flex flex-col items-center gap-1 relative">
                              <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ring-2 ring-surface-container-lowest z-10 relative ${isActive ? "bg-primary border border-surface-container-lowest text-on-primary" : isPassed ? "bg-primary text-on-primary" : "bg-surface-container-high border-2 border-outline-variant"}`}>
                                {isActive && <div className="absolute -inset-0.5 rounded-full bg-primary/45 timeline-pulse"></div>}
                                {isPassed && <span className="material-symbols-outlined text-[8px] font-bold">check</span>}
                                {isActive && <div className="w-1 h-1 bg-on-primary rounded-full relative z-10"></div>}
                              </div>
                              <span className={`font-label-code text-[9px] ${isActive ? "text-primary font-bold" : "text-on-surface-variant"}`}>{stg}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3 mt-4 max-h-[170px] overflow-y-auto pr-2">
                    {activeRow.prediction.pest.recommendations && activeRow.prediction.pest.recommendations.trim() ? (
                      activeRow.prediction.pest.recommendations.split("\n\n").map((para, idx) => {
                        const isTreatment = para.startsWith("Treatment:");
                        const isPrevention = para.startsWith("Prevention:");
                        let content = para;
                        let label = "";
                        if (isTreatment) {
                          label = "Treatment Plan:";
                          content = para.replace("Treatment:", "").trim();
                        } else if (isPrevention) {
                          label = "Preventive Actions:";
                          content = para.replace("Prevention:", "").trim();
                        }
                        return (
                          <div key={idx} className="mb-3 last:mb-0">
                            {label && <span className="font-bold text-on-surface text-xs block mb-0.5">{label}</span>}
                            {renderFormattedPoints(content, "text-xs")}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-on-surface-variant/75 text-xs">No treatments recommended</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2 mt-4">
                  <button
                    onClick={handleFlagFolderImage}
                    disabled={flaggedImages[activeIndex] || loading}
                    className={`w-full flex items-center justify-center gap-2 font-body-sm text-body-sm py-2 px-4 rounded-lg transition-all border ${flaggedImages[activeIndex]
                        ? "bg-primary/10 text-primary border-primary/20 cursor-default"
                        : "bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 cursor-pointer"
                      }`}
                  >
                    <span className="material-symbols-outlined text-[18px]">
                      {flaggedImages[activeIndex] ? "check_circle" : "flag"}
                    </span>
                    {flaggedImages[activeIndex] ? "Verification Flagged" : "Flag for Verification"}
                  </button>

                  <div className="flex gap-2 w-full">
                    <button
                      disabled={activeIndex === 0}
                      onClick={() => setActiveIndex(prev => prev - 1)}
                      className="flex-1 py-2 border border-outline text-on-surface rounded-lg font-label-code text-label-code disabled:opacity-40 hover:bg-surface-variant/20 transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      disabled={activeIndex === processedResults.length - 1}
                      onClick={() => setActiveIndex(prev => prev + 1)}
                      className="flex-1 py-2 bg-primary text-on-primary rounded-lg font-label-code text-label-code disabled:opacity-40 hover:bg-primary/95 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Carousel Strip */}
            <div className="shrink-0 bg-surface-container-lowest rounded-xl border border-outline-variant/20 p-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveIndex(prev => Math.max(prev - 1, 0))}
                  disabled={activeIndex === 0}
                  className="p-1.5 rounded-full hover:bg-surface-variant text-on-surface-variant transition-colors disabled:opacity-30"
                >
                  <span className="material-symbols-outlined text-xl">chevron_left</span>
                </button>
                <div ref={thumbnailStripRef} className="flex-1 overflow-x-auto flex gap-1.5 py-0.5 hide-scrollbar">
                  {processedResults.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => setActiveIndex(idx)}
                      className={`shrink-0 relative w-14 h-14 rounded-md overflow-hidden border-2 cursor-pointer transition-all ${idx === activeIndex ? "border-primary scale-105" : "border-outline-variant/30 opacity-70 hover:opacity-100"}`}
                    >
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setActiveIndex(prev => Math.min(prev + 1, processedResults.length - 1))}
                  disabled={activeIndex === processedResults.length - 1}
                  className="p-1.5 rounded-full hover:bg-surface-variant text-on-surface-variant transition-colors disabled:opacity-30"
                >
                  <span className="material-symbols-outlined text-xl">chevron_right</span>
                </button>
              </div>
            </div>

            {/* Bottom: Table & Tabbed Summary */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-gutter mt-4">
              {/* Infestations Queue Table */}
              <div className="md:col-span-8 bg-surface-container-lowest rounded-xl border border-outline-variant/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-outline-variant/20 flex justify-between items-center bg-surface-bright/50">
                  <h3 className="font-headline-sm text-on-surface font-semibold">Report Analysis Queue</h3>
                  <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-[12px] font-medium">{processedResults.length} Items</span>
                </div>
                <div className="overflow-y-auto max-h-[400px]">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 bg-surface-container-lowest z-10 border-b border-outline-variant/20">
                      <tr>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Filename</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Crop</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Threat</th>
                        <th className="py-3 px-6 text-label-code text-on-surface-variant font-medium">Stage</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10 text-body-sm">
                      {processedResults.map((row, idx) => {
                        const isRowActive = idx === activeIndex;
                        const diseaseActive = row.prediction.pest.disease && row.prediction.pest.disease.toLowerCase() !== "none" && row.prediction.pest.disease.toLowerCase() !== "healthy" && row.prediction.pest.disease.toLowerCase() !== "no disease found";
                        const pestActive = row.prediction.pest.pest && row.prediction.pest.pest.toLowerCase() !== "none" && row.prediction.pest.pest.toLowerCase() !== "no pest found";

                        let displayThreat = "None";

                        if (diseaseActive && pestActive) {
                          displayThreat = `${row.prediction.pest.disease} / ${row.prediction.pest.pest} (${((row.prediction.pest.diseaseConfidence || 0.0) * 100).toFixed(0)}/${((row.prediction.pest.pestConfidence || 0.0) * 100).toFixed(0)}%)`;
                        } else if (diseaseActive) {
                          displayThreat = `${row.prediction.pest.disease} (${((row.prediction.pest.diseaseConfidence || 0.0) * 100).toFixed(0)}%)`;
                        } else if (pestActive) {
                          displayThreat = `${row.prediction.pest.pest} (${((row.prediction.pest.pestConfidence || 0.0) * 100).toFixed(0)}%)`;
                        }

                        return (
                          <tr key={idx} onClick={() => setActiveIndex(idx)}
                            className={`cursor-pointer transition-colors ${isRowActive ? "bg-primary/5 text-primary font-medium" : "hover:bg-surface-container-low"}`}>
                            <td className="py-3 px-6 flex items-center gap-2">
                              <span className="material-symbols-outlined text-[16px] text-outline-variant">image</span>
                              {row.filename}
                            </td>
                            <td className="py-3 px-6 text-primary font-medium">{row.prediction.crop.label}</td>
                            <td className="py-3 px-6 text-error">{displayThreat}</td>
                            <td className="py-3 px-6 text-secondary">{row.prediction.stage.label}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Count Summary with 3 Tabs */}
              <div className="md:col-span-4 bg-secondary text-on-secondary rounded-xl p-5 flex flex-col justify-between min-h-[300px]">
                <div>
                  <h3 className="font-headline-md text-headline-md mb-3">Batch Summary</h3>
                  <div className="flex items-end gap-3 mb-6">
                    <span className="text-5xl font-bold tracking-tight">{processedResults.length}</span>
                    <span className="font-body-md text-secondary-fixed-dim pb-1">Total Scanned</span>
                  </div>

                  {/* Tabs selector */}
                  <div className="flex bg-on-secondary/10 p-1 rounded-lg mb-4">
                    <button
                      onClick={() => setSummaryTab("crop")}
                      className={`flex-1 py-1 text-center font-label-code text-[11px] rounded transition-all ${summaryTab === "crop" ? "bg-surface-container-lowest text-secondary font-bold shadow-sm" : "text-on-secondary/70 hover:text-white"}`}
                    >
                      Crop
                    </button>
                    <button
                      onClick={() => setSummaryTab("stage")}
                      className={`flex-1 py-1 text-center font-label-code text-[11px] rounded transition-all ${summaryTab === "stage" ? "bg-surface-container-lowest text-secondary font-bold shadow-sm" : "text-on-secondary/70 hover:text-white"}`}
                    >
                      Stage
                    </button>
                    <button
                      onClick={() => setSummaryTab("infestation")}
                      className={`flex-1 py-1 text-center font-label-code text-[11px] rounded transition-all ${summaryTab === "infestation" ? "bg-surface-container-lowest text-secondary font-bold shadow-sm" : "text-on-secondary/70 hover:text-white"}`}
                    >
                      Threat
                    </button>
                  </div>

                  {/* Tab contents distribution */}
                  <div className="overflow-y-auto space-y-4 pr-1 max-h-[180px]">
                    {(() => {
                      const counts = {};
                      processedResults.forEach(r => {
                        let key = "Unknown";
                        if (summaryTab === "crop") {
                          key = r.prediction.crop.label || "Unknown";
                        } else if (summaryTab === "stage") {
                          key = r.prediction.stage.label || "Unknown";
                        } else if (summaryTab === "infestation") {
                          key = r.prediction.pest.disease || "Unknown";
                        }
                        counts[key] = (counts[key] || 0) + 1;
                      });

                      return Object.entries(counts).map(([label, count]) => (
                        <div key={label}>
                          <div className="flex justify-between font-body-md mb-1.5 font-sans">
                            <span className="truncate max-w-[150px] font-medium">{label}</span>
                            <span className="shrink-0 ml-2 font-bold">{count}</span>
                          </div>
                          <div className="w-full bg-on-secondary-fixed-variant h-2 rounded-full overflow-hidden">
                            <div
                              className="bg-error-container h-full rounded-full transition-all duration-300"
                              style={{ width: `${(count / processedResults.length) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                  <button
                    onClick={() => anotherInputRef.current?.click()}
                    className="w-full bg-surface-container-lowest text-secondary font-label-code text-label-code py-2.5 px-4 rounded-lg hover:bg-surface-bright transition-all shadow-sm flex items-center justify-center gap-1.5"
                    disabled={loading}
                  >
                    <span className="material-symbols-outlined text-[16px]">add_a_photo</span> Predict Another Image
                  </button>
                  <button
                    onClick={() => anotherFolderInputRef.current?.click()}
                    className="w-full bg-transparent border border-on-secondary/35 text-on-secondary hover:bg-on-secondary/10 py-2.5 rounded-lg font-label-code text-label-code flex items-center justify-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-[16px]">folder</span> Predict Another Folder
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null
      )}

      {/* STATE 4: Crop Catalog Reference */}
      {uiState === 4 && (
        <div className="px-6 py-4 max-w-7xl mx-auto w-full flex-1 flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-headline-lg text-headline-lg text-on-background mb-1">CROP CATALOG</h3>
              <p className="font-body-sm text-body-sm text-on-surface-variant">Reference of crop types, symptoms, and growth lifecycle stages.</p>
            </div>

            <div className="relative w-full sm:w-72">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-lg">search</span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search crops or diseases..."
                className="w-full pl-10 pr-8 py-2 rounded-xl border border-outline-variant/30 bg-surface-container-low text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-xs"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface">
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-gutter">
            {filteredCrops.map((crop) => (
              <div key={crop.name} className="glass-card rounded-2xl overflow-hidden border border-outline-variant/20 hover-lift transition-all duration-300">
                <div className="h-36 w-full relative overflow-hidden bg-surface-container">
                  <img src={crop.image} alt={crop.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 flex items-center gap-2">
                    <span className="material-symbols-outlined text-white text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>{crop.icon}</span>
                    <h3 className="text-white font-semibold text-lg">{crop.name}</h3>
                  </div>
                  <div className="absolute top-3 right-3 flex gap-1.5">
                    <span className="bg-error/80 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">bug_report</span>
                      {crop.diseases.filter(d => d.name !== "Healthy").length}
                    </span>
                    <span className="bg-primary/80 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px]">timeline</span>
                      {crop.stages.length}
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <div className="mb-3">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-on-surface-variant mb-1.5 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px] text-error">bug_report</span>
                      Diseases & Pests
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {crop.diseases.filter(d => d.name !== "Healthy").map((d) => (
                        <span key={d.key} className="inline-flex items-center px-2 py-0.5 rounded bg-surface-container-high text-on-surface text-[10px] font-medium border border-outline-variant/15">
                          {d.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-on-surface-variant mb-1.5 flex items-center gap-1">
                      <span className="material-symbols-outlined text-[12px] text-primary">timeline</span>
                      Growth Stages
                    </p>
                    <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
                      {crop.stages.map((stage, idx) => (
                        <React.Fragment key={stage}>
                          <span className="text-[9px] text-on-surface-variant whitespace-nowrap px-1.5 py-0.5 rounded bg-surface-container font-medium">{stage}</span>
                          {idx < crop.stages.length - 1 && (
                            <span className="material-symbols-outlined text-[10px] text-outline-variant shrink-0">arrow_forward</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fullscreen Overlay */}
      {isFullscreen && singleResult && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-6">
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-6 right-6 text-white hover:text-primary transition-colors bg-white/10 p-2 rounded-full"
          >
            <span className="material-symbols-outlined text-3xl">close</span>
          </button>
          <img
            src={getImageUrl(singleResult.image)}
            alt="Fullscreen report view"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
      <VerifiedReportsModal
        isOpen={showVerifiedModal}
        onClose={() => setShowVerifiedModal(false)}
        tickets={userTickets}
        onAcknowledge={async (ticketIdOrIds) => {
          const sessionId = localStorage.getItem("cropscan_session_id");
          const ids = Array.isArray(ticketIdOrIds) ? ticketIdOrIds : [ticketIdOrIds];
          await Promise.all(ids.map(id => acknowledgeVerification(sessionId, id)));
          await loadTicketsAndHistory();
        }}
      />

      {/* Hidden inputs */}
      {/* Hidden inputs always in DOM to preserve ref connectivity across states */}
      <input
        type="file"
        accept="image/*"
        ref={anotherInputRef}
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            handleUploadImage(e.target.files[0]);
          }
        }}
      />
      <input
        ref={anotherFolderInputRef}
        type="file"
        webkitdirectory="true"
        directory="true"
        multiple
        className="hidden"
        onChange={handleFolderUpload}
      />
    </div>
  );
}
